"use client";

import * as React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, CircleQuestionMark as HelpCircle, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackArticles = [{
  id: "fallback-1",
  title: "Can dogs safely eat blueberries?",
  slug: "can-dogs-eat-blueberries",
  excerpt: "Blueberries offer rich antioxidants and fiber for dogs. Learn the safe portion limits and preparation advice.",
  category: "Nutrition",
  readTime: "4 min read",
  reviewedBy: "Dr. Elena Vance, DVM",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/pet_nutrition_dog_blueberries.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/pet_nutrition_dog_blueberries.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/pet_nutrition_dog_blueberries.png-wvc-sizes",
    alt: "Golden retriever looking at a bowl of fresh blueberries"
  }
}, {
  id: "fallback-2",
  title: "Why is my dog licking his paws constantly?",
  slug: "why-dog-licking-paws",
  excerpt: "Frequent paw licking often points to environmental allergies, minor skin irritation, or stress triggers.",
  category: "Behavior & Health",
  readTime: "5 min read",
  reviewedBy: "Dr. Marcus Chen, BVSc",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_licking_paws_examination.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_licking_paws_examination.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_licking_paws_examination.png-wvc-sizes",
    alt: "Close-up of a dog resting comfortably during a paw health check"
  }
}, {
  id: "fallback-3",
  title: "Why is my cat suddenly not eating?",
  slug: "why-cat-not-eating",
  excerpt: "Feline appetite loss requires rapid attention. Discover common clinical causes and when to consult your clinic.",
  category: "Clinical Care",
  readTime: "4 min read",
  reviewedBy: "Dr. Sarah Jenkins, DVM",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_health_nutrition_check.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_health_nutrition_check.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_health_nutrition_check.png-wvc-sizes",
    alt: "Domestic shorthair cat sitting near a stainless steel food bowl"
  }
}];
function QuestionsConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 3
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-xl border border-border bg-card p-6 shadow-xs animate-pulse">
            <div className="h-48 w-full rounded-lg bg-muted" />
            <div className="mt-4 h-4 w-24 rounded-md bg-muted" />
            <div className="mt-3 h-6 w-3/4 rounded-md bg-muted" />
            <div className="mt-2 h-4 w-full rounded-md bg-muted" />
            <div className="mt-6 flex items-center justify-between pt-4">
              <div className="h-4 w-20 rounded-md bg-muted" />
              <div className="h-4 w-28 rounded-md bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  const hasLivePosts = posts && posts.length > 0;
  if (hasLivePosts) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const categoryName = post.categories?.[0]?.name || "Pet Health";
        const linkTarget = post.link || "/PetHealthPage";
        return <article key={post.id} data-index={i} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-background transition-[color,background-color,border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-border hover:shadow-md">
              <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title || "Pet wellness guide"} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-103" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                    <HelpCircle className="size-10 stroke-muted-foreground" />
                  </div>}
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="bg-background/90 text-foreground font-semibold backdrop-blur-xs">
                    {categoryName}
                  </Badge>
                </div>
              </div>

              <Card className="flex flex-1 flex-col justify-between border-0 bg-transparent p-6 shadow-none">
                <CardHeader className="p-0">
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-primary">
                    <CheckCircle2 className="size-3.5 text-chart-1" />
                    <span>Veterinary verified</span>
                  </div>
                  <h3 className="font-serif text-xl leading-snug font-semibold text-foreground transition-colors group-hover:text-primary">
                    {post.title}
                  </h3>
                </CardHeader>

                <CardContent className="p-0 pt-3">
                  {post.excerpt && <p className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                      {post.excerpt.replace(/<[^>]+>/g, "")}
                    </p>}
                </CardContent>

                <CardFooter className="flex items-center justify-between border-t border-border p-0 pt-4 mt-6">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="size-3.5" />
                    4 min read
                  </span>
                  <Link to={linkTarget} className="inline-flex items-center gap-1 text-xs font-semibold text-foreground transition-colors hover:text-chart-1">
                    Read answer
                    <ArrowRight className="size-3.5" />
                  </Link>
                </CardFooter>
              </Card>
            </article>;
      })}
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {fallbackArticles.map((article, i) => <article key={article.id} data-index={i} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-background transition-[color,background-color,border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-border hover:shadow-md">
          <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
            <img src={article.image.src} data-wvc-srcset={article.image.srcset} data-wvc-sizes={article.image.sizes} alt={article.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-103" />
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center rounded-full bg-background/90 px-3 py-0.5 text-xs font-semibold text-foreground backdrop-blur-xs border border-border">
                {article.category}
              </span>
            </div>
          </div>

          <div className="flex flex-1 flex-col justify-between p-6">
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                <CheckCircle2 className="size-3.5 text-chart-1" />
                <span>Reviewed by {article.reviewedBy}</span>
              </div>
              <h3 className="font-serif text-xl leading-snug font-semibold text-foreground">
                <Link to="/PetHealthPage" className="hover:text-chart-1 transition-colors">
                  {article.title}
                </Link>
              </h3>
              <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                {article.excerpt}
              </p>
            </div>

            <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="size-3.5" />
                {article.readTime}
              </span>
              <Button variant="ghost" size="sm" asChild className="h-auto p-0 font-semibold text-foreground hover:bg-transparent hover:text-chart-1">
                <Link to="/PetHealthPage" className="inline-flex items-center gap-1 text-xs">
                  Read clinical answer
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </div>
        </article>)}
    </div>;
}
export default function Questions() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="popular-pet-questions" data-nav="light" className="w-full bg-background py-16 md:py-20 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-10 flex flex-col justify-between gap-4 md:mb-12 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="mb-2.5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-chart-1">
              <ShieldCheck className="size-4" />
              <span>Evidence-based clinical guidance</span>
            </div>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Common pet questions, answered by vets
            </h2>
            <p className="mt-2.5 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Clear, research-backed advice for the daily health and dietary questions every dog and cat owner encounters.
            </p>
          </div>

          <div className="shrink-0">
            <Button variant="outline" asChild className="border-border text-foreground hover:bg-muted">
              <Link to="/PetHealthPage" className="inline-flex items-center gap-2 text-sm font-semibold">
                Explore all questions
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Clinical Governance Callout */}
        <div className="mb-8 rounded-lg border border-border border-l-4 border-l-chart-1 bg-card p-4 sm:p-5">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-semibold text-foreground sm:text-sm">
              <strong className="font-semibold text-chart-1">Clinical governance standard:</strong> All medical summaries undergo strict review by licensed veterinary practitioners prior to publication.
            </p>
            <Link to="/AboutPetsWhirlPage" className="text-xs font-semibold text-muted-foreground underline underline-offset-4 hover:text-foreground">
              Our review process
            </Link>
          </div>
        </div>

        {/* Dynamic / Fallback Grid Container */}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }} className="w-full">
          <QuestionsConsumer />
        </PostsProvider>
      </div>
    </section>;
}